import { Controller } from '@hotwired/stimulus'
import L from 'leaflet'

export default class extends Controller {
  static targets = ['map', 'caption']
  static values = {
    samples: Array,
    location: Object,
    startLocation: Object,
    endLocation: Object,
    points: Array,
    pinColorClass: { type: String, default: 'text-custom-green' }
  }

  connect () {
    this.map = L.map(this.mapTarget).setView([39.8283, -98.5795], 4)
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '© OpenStreetMap contributors'
    }).addTo(this.map)
    const segments = this.segments()
    const bounds = segments.flat()
    const endpoints = [
      { location: this.startLocationValue, kind: 'start', label: 'Start' },
      { location: this.endLocationValue, kind: 'end', label: 'End' }
    ].filter(endpoint => endpoint.location.latitude != null)
    if (segments.length) {
      segments.forEach(segment => L.polyline(segment, { color: '#36852b', weight: 5 }).addTo(this.map))
      this.captionTarget.textContent = 'Recorded route segments. GPS gaps and low-accuracy readings are not connected; this may be a partial route.'
    } else if (endpoints.length) {
      this.captionTarget.textContent = ''
    } else if (this.locationValue.latitude != null) {
      this.addFlag(this.locationValue, 'approximate', 'Walk')
      bounds.push([this.locationValue.latitude, this.locationValue.longitude])
      this.captionTarget.textContent = 'Approximate walk location: the midpoint of the start and finish, or the single available location. Not a recorded route.'
    } else {
      this.captionTarget.textContent = 'No GPS location was available. Your prayer walk time is still recorded.'
    }
    this.captionTarget.hidden = !this.captionTarget.textContent
    endpoints.forEach(endpoint => {
      this.addFlag(endpoint.location, endpoint.kind, endpoint.label)
      bounds.push([endpoint.location.latitude, endpoint.location.longitude])
    })
    this.pointsValue.forEach(point => {
      const position = [point.latitude, point.longitude]
      L.marker(position, {
        title: point.name,
        alt: point.name,
        icon: L.divIcon({
          className: `nature-map-pin nature-map-pin--prayer-walk ${this.pinColorClassValue}`,
          html: '<span class="nature-map-pin__marker"></span>',
          iconSize: [28, 28],
          iconAnchor: [14, 28]
        })
      }).addTo(this.map).bindPopup(() => this.pointPopup(point), { className: 'prayer-point-popup' })
      bounds.push(position)
    })
    // Turbo can connect this controller before the inline walk panel has its final size.
    const fitMap = () => {
      if (!this.map || !this.mapTarget.clientWidth || !this.mapTarget.clientHeight) return
      this.map.invalidateSize({ pan: false })
      if (bounds.length) {
        this.map.fitBounds(L.latLngBounds(bounds), { padding: [90, 45], maxZoom: segments.length ? 17 : 15, animate: false })
      }
    }
    this.fitFrame = window.requestAnimationFrame(fitMap)
    if (window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(fitMap)
      this.resizeObserver.observe(this.mapTarget)
    }
  }

  addFlag (location, kind, label) {
    L.marker([location.latitude, location.longitude], {
      title: `${label} location`,
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: `prayer-walk-flag prayer-walk-flag--${kind}`,
        html: '<svg viewBox="0 0 24 32" aria-hidden="true">' +
          '<path d="M4 3h15l-4 6 4 6H4Z" fill="currentColor"/>' +
          '<path d="M4 2v28" fill="none" stroke="currentColor" stroke-width="2"/>' +
          `</svg><span>${label}</span>`,
        iconSize: [80, 32],
        iconAnchor: kind === 'end' ? [76, 32] : [4, 32]
      })
    }).addTo(this.map)
  }

  disconnect () {
    window.cancelAnimationFrame(this.fitFrame)
    this.resizeObserver?.disconnect()
    this.map?.remove()
    this.map = null
  }

  pointPopup (point) {
    const content = document.createElement('div')
    const name = document.createElement('strong')
    name.textContent = point.name
    const category = document.createElement('p')
    category.textContent = point.category.replaceAll('_', ' ').replace(/^./, letter => letter.toUpperCase())
    const count = document.createElement('p')
    count.textContent = `🙏 ${point.prayerCount}x`
    count.setAttribute('aria-label', `${point.prayerCount} total prayers at this location`)
    content.append(name, category, count)
    return content
  }

  segments () {
    const segments = []
    let current = []
    let previous = null
    for (const sample of this.samplesValue) {
      const time = Date.parse(sample.recorded_at)
      if ((sample.accuracy != null && sample.accuracy > 100) ||
          (previous && time - previous > 90000)) {
        if (current.length > 1) segments.push(current)
        current = []
      }
      if (sample.accuracy == null || sample.accuracy <= 100) current.push([sample.latitude, sample.longitude])
      previous = time
    }
    if (current.length > 1) segments.push(current)
    return segments
  }
}
